import { useState } from 'react';
import PropTypes from 'prop-types';
import { useAuth } from '../contexts/AuthContext';

const PdfIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="w-6 h-6">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);

const FileUploadModal = ({ onClose, onUpload }) => {
  const [title, setTitle] = useState('');
  const [file, setFile] = useState(null);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const { currentUser } = useAuth();

  // Validate user has permission to upload
  if (!currentUser?.profile?.isEditor) {
    return null;
  }

  const validateFile = (selectedFile) => {
    // Validate file type
    const isImage = selectedFile.type.startsWith('image/');
    const isPdf = selectedFile.type === 'application/pdf';
    if (!isImage && !isPdf) {
      throw new Error('Please select an image (JPG, PNG, WebP) or PDF file');
    }

    // Validate file size (5MB)
    if (selectedFile.size > 5 * 1024 * 1024) {
      throw new Error('File size must be less than 5MB');
    }

    // Validate file name for security
    const fileName = selectedFile.name.toLowerCase();
    const validExtensions = ['.jpg', '.jpeg', '.png', '.webp', '.pdf'];
    if (!validExtensions.some(ext => fileName.endsWith(ext))) {
      throw new Error('Invalid file type. Only JPG, PNG, WebP, and PDF files are allowed.');
    }
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    try {
      validateFile(selectedFile);
      setFile(selectedFile);
      setError('');
    } catch (err) {
      setError(err.message);
      setFile(null);
    }
  };

  const validateTitle = (title) => {
    if (!title || title.trim().length === 0) {
      throw new Error('Title is required');
    }
    if (title.trim().length > 100) {
      throw new Error('Title must be less than 100 characters');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      validateTitle(title);
      if (!file) {
        throw new Error('Please select a file');
      }

      setUploading(true);
      await onUpload({ file, title: title.trim() });
      onClose();
    } catch (error) {
      setError(error.message);
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-surface dark:bg-surface-dark rounded-lg p-6 max-w-md w-full z-[60]">
        <h2 className="text-xl font-bold mb-4 text-primary dark:text-primary-dark">Upload Base Map</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-secondary dark:text-secondary-dark mb-1">
              Title
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full p-2 rounded-md bg-surface dark:bg-surface-dark text-primary dark:text-primary-dark border-default dark:border-default-dark"
              placeholder="e.g., Crew Quarters at KAPA"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-secondary dark:text-secondary-dark mb-1">
              File
            </label>
            <div className="flex flex-col gap-2">
              <input
                type="file"
                onChange={handleFileSelect}
                accept="image/*,.pdf"
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark inline-flex items-center justify-center"
              >
                Choose File
              </label>
              {file && (
                <div className="text-sm text-secondary dark:text-secondary-dark">
                  Selected: {file.name}
                </div>
              )}
              <p className="text-xs text-secondary dark:text-secondary-dark">
                Max size: 5MB. Supported formats: JPG, PNG, WebP, PDF
              </p>
              <p className="text-xs text-secondary dark:text-secondary-dark">
                Note: Please upload from a computer for best results
              </p>
            </div>
          </div>

          {error && (
            <p className="text-status-error dark:text-status-error-dark text-sm">
              {error}
            </p>
          )}

          <div className="flex justify-end gap-2 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-secondary hover:text-secondary-hover dark:text-secondary-dark dark:hover:text-secondary-hover-dark"
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark disabled:opacity-50"
              disabled={uploading}
            >
              {uploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FileViewModal = ({ file, onClose, onDelete, onTitleEdit, canDelete, onNext, onPrevious, hasNext, hasPrevious }) => {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(file.title);
  const { currentUser } = useAuth();

  const validateTitle = (title) => {
    if (!title || title.trim().length === 0) {
      throw new Error('Title is required');
    }
    if (title.trim().length > 100) {
      throw new Error('Title must be less than 100 characters');
    }
  };

  const handleSaveTitle = async () => {
    try {
      validateTitle(editedTitle);
      await onTitleEdit({
        ...file,
        title: editedTitle.trim()
      });
      setIsEditingTitle(false);
    } catch (error) {
      console.error('Error updating file title:', error);
      // Reset to original title on error
      setEditedTitle(file.title);
      setIsEditingTitle(false);
    }
  };

  // Ensure user has permission to view
  if (!currentUser?.profile?.isEditor && !currentUser?.profile?.viewSecret) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center p-4 z-50">
      <div className="relative w-full h-full flex items-center justify-center z-[60]">
        {canDelete && (
          <button
            onClick={onDelete}
            className="absolute left-4 top-4 p-2 bg-black bg-opacity-70 rounded-lg text-red-400 hover:text-red-300 transition-colors"
            title="Delete file"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}

        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 bg-black bg-opacity-70 rounded-lg text-white hover:text-gray-300 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {hasPrevious && (
          <button
            onClick={onPrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}

        {hasNext && (
          <button
            onClick={onNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        )}

        <div className="max-w-5xl w-full">
          <div className="text-white mb-2 text-center flex items-center justify-center gap-2">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editedTitle}
                  onChange={(e) => setEditedTitle(e.target.value)}
                  className="bg-black bg-opacity-50 text-white px-2 py-1 rounded border border-white/30 text-center"
                  autoFocus
                />
                <button
                  onClick={handleSaveTitle}
                  className="p-1 text-white hover:text-green-400"
                  title="Save title"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  onClick={() => {
                    setIsEditingTitle(false);
                    setEditedTitle(file.title);
                  }}
                  className="p-1 text-white hover:text-red-400"
                  title="Cancel"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <span>{file.title}</span>
                {canDelete && (
                  <button
                    onClick={() => setIsEditingTitle(true)}
                    className="p-1 text-white hover:text-blue-400"
                    title="Edit title"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                  </button>
                )}
              </>
            )}
          </div>
          {file.fileType === 'pdf' ? (
            <iframe
              src={`${file.fileUrl}#toolbar=0`}
              className="w-full h-[80vh] bg-white rounded-lg"
              title={file.title}
            />
          ) : (
            <img
              src={file.fileUrl}
              alt={file.title}
              className="max-h-[80vh] mx-auto object-contain"
            />
          )}
        </div>
      </div>
    </div>
  );
};

const BaseMaps = ({ airportId, baseMaps = [], onUpload, onDelete, onTitleEdit }) => {
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const { currentUser } = useAuth();
  const isEditor = currentUser?.profile?.isEditor;
  const canView = isEditor || currentUser?.profile?.viewSecret;

  // Ensure user has permission to view
  if (!canView) {
    return null;
  }

  const handleUpload = async (fileData) => {
    await onUpload(fileData);
  };

  const handleDelete = async (file) => {
    // Ensure user has permission to delete
    if (!isEditor) {
      console.error('Permission denied: Only editors can delete files');
      return;
    }

    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        await onDelete(file);
        setSelectedFile(null);
      } catch (error) {
        console.error('Error deleting file:', error);
      }
    }
  };

  const handleTitleEdit = async (updatedFile) => {
    try {
      // Update the file in the parent component
      await onTitleEdit(updatedFile);
      // Update the selected file with the new title
      setSelectedFile(updatedFile);
    } catch (error) {
      console.error('Error updating file title:', error);
    }
  };

  const currentIndex = selectedFile ? baseMaps.findIndex(f => f.id === selectedFile.id) : -1;

  return (
    <section className={`${(!currentUser?.profile?.isEditor && !currentUser?.profile?.viewSecret) ? 'hidden' : ''}`}>
      <div className="flex justify-between items-center mb-3">
        <h2 className="text-xl font-bold text-primary dark:text-primary-dark">
          Base Maps
        </h2>
        {isEditor && (
          <button
            onClick={() => setShowUploadModal(true)}
            className="px-4 py-2 bg-button text-white rounded-md hover:bg-button-hover dark:bg-button-dark dark:hover:bg-button-hover-dark flex items-center gap-2"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
              <path d="M10.75 4.75a.75.75 0 00-1.5 0v4.5h-4.5a.75.75 0 000 1.5h4.5v4.5a.75.75 0 001.5 0v-4.5h4.5a.75.75 0 000-1.5h-4.5v-4.5z" />
            </svg>
            Add File
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {baseMaps.map((file) => (
          <div
            key={file.id}
            onClick={() => setSelectedFile(file)}
            className="cursor-pointer group relative aspect-square rounded-lg overflow-hidden bg-surface-hover dark:bg-surface-hover-dark"
          >
            {file.fileType === 'pdf' ? (
              <div className="w-full h-full flex items-center justify-center bg-surface dark:bg-surface-dark">
                <PdfIcon />
              </div>
            ) : (
              <img
                src={file.thumbnailUrl || file.fileUrl}
                alt={file.title}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            )}
            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-opacity" />
            <div className="absolute bottom-0 left-0 right-0 p-2 bg-black bg-opacity-50 text-white">
              <div className="text-sm truncate">{file.title}</div>
              <div className="text-xs opacity-75">
                {new Date(file.uploadedAt).toLocaleDateString()}
              </div>
            </div>
          </div>
        ))}
      </div>

      {showUploadModal && (
        <FileUploadModal
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUpload}
        />
      )}

      {selectedFile && (
        <FileViewModal
          file={selectedFile}
          onClose={() => setSelectedFile(null)}
          onDelete={() => handleDelete(selectedFile)}
          onTitleEdit={handleTitleEdit}
          canDelete={isEditor}
          onNext={() => currentIndex < baseMaps.length - 1 && setSelectedFile(baseMaps[currentIndex + 1])}
          onPrevious={() => currentIndex > 0 && setSelectedFile(baseMaps[currentIndex - 1])}
          hasNext={currentIndex < baseMaps.length - 1}
          hasPrevious={currentIndex > 0}
        />
      )}
    </section>
  );
};

FileUploadModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
};

FileViewModal.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    fileUrl: PropTypes.string.isRequired,
    fileType: PropTypes.oneOf(['image', 'pdf']).isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onTitleEdit: PropTypes.func,
  canDelete: PropTypes.bool,
  onNext: PropTypes.func,
  onPrevious: PropTypes.func,
  hasNext: PropTypes.bool,
  hasPrevious: PropTypes.bool,
};

BaseMaps.propTypes = {
  airportId: PropTypes.string.isRequired,
  baseMaps: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    fileUrl: PropTypes.string.isRequired,
    thumbnailUrl: PropTypes.string,
    fileType: PropTypes.oneOf(['image', 'pdf']).isRequired,
    uploadedAt: PropTypes.string.isRequired,
  })),
  onUpload: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onTitleEdit: PropTypes.func.isRequired,
};

export default BaseMaps; 